import { useMutation, UseQueryResult } from '@tanstack/react-query';
import { createBudgetQueryKeys } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { RQAPICallWithData } from 'store/APIClient';
import { getArgumentsEndpoint } from 'routes/BudgetModule/components/BudgetProposal/Resource/Analyse/helpers/getArgumentsEndpoint';
import { ArgumentT } from 'routes/BudgetModule/config/types';
import { FilterConfigT } from './steps/ThirdDuplicateArgumentsStep';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/browser';
import { useArgumentQuery } from 'routes/BudgetModule/helpers/hooks/useCustomQuery';
import { useTranslation } from 'react-i18next';

export const useGetArguments = (
    budgetProposalId?: number,
    resourceSoftwareId?: string,
): UseQueryResult<ArgumentT[]> => {
    const { argumentKeys } = createBudgetQueryKeys();
    const params = {
        enabled:
            budgetProposalId !== undefined && resourceSoftwareId !== undefined,
        initialData: [],
    };

    return useArgumentQuery(
        argumentKeys({ budgetProposalId, resourceSoftwareId }),
        async () => {
            const response = await RQAPICallWithData<ArgumentOptionsT[]>({
                endpoint: getArgumentsEndpoint({
                    budgetProposalId: budgetProposalId?.toString() ?? '',
                    resourceSoftwareId: resourceSoftwareId ?? '',
                }),
                method: 'GET',
            });

            return response.content.data;
        },
        params,
    );
};

export const useDuplicateArguments = (
    argumentIds?: number[],
    budgetProposalIds?: number[],
    resourceSoftwareIds?: string[],
    shouldAdaptFilters?: FilterConfigT,
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async () => {
            if (
                argumentIds === undefined ||
                budgetProposalIds === undefined ||
                resourceSoftwareIds === undefined ||
                shouldAdaptFilters === undefined
            ) {
                Sentry.captureException(
                    new Error(
                        `Hook useDuplicateArguments being called with invalid arguments: ${argumentIds}, ${budgetProposalIds}, ${resourceSoftwareIds}, ${shouldAdaptFilters}`,
                    ),
                );
                return null;
            }
            const response = await RQAPICallWithData<ArgumentT[]>({
                endpoint: `budget_proposals/arguments/duplicate`,
                method: 'POST',
                body: {
                    argumentIds,
                    budgetProposalIds,
                    resourceSoftwareIds,
                    shouldAdaptFilters: {
                        ...shouldAdaptFilters,
                        exercice: true,
                    },
                },
            });
            return response.content.data;
        },

        onSuccess: (data) => {
            dispatch.vision.notifyUser({
                message: t(
                    'adminBudgetSpace.duplicateArguments.successMessage',
                    { count: data?.length },
                ),
                snackType: 'success',
            });
        },

        meta: {
            useGlobalOnError: true,
        },
    });
};
type ArgumentOptionsT = {
    key: number;
    label: string;
    value: ArgumentT;
};
